/**
 * Gap protection / anti-vaskning validation.
 * Prevents bookings that create unusable gaps in the schedule.
 */

function timeToMinutes(timeStr: string): number {
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + (m || 0);
}

function dateToMinutes(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

export interface GapProtectionSettings {
  gap_protection?: boolean;
  min_gap_minutes?: number;
  fixed_start_times?: boolean;
  start_interval_minutes?: number;
  fixed_durations?: boolean;
  allowed_durations?: number[];
}

/**
 * Check if a proposed booking would create an unusable gap.
 * Returns error message string if invalid, or null if OK.
 */
export function validateGapProtection(
  courtId: string,
  proposedStart: Date,
  proposedEnd: Date,
  existingBookings: Array<{ court_id: string; start_time: string; end_time: string; status?: string }>,
  hallOpenTime: string,
  hallCloseTime: string,
  settings: GapProtectionSettings
): string | null {
  if (!settings.gap_protection) return null;

  const minGap = settings.min_gap_minutes ?? 60;
  if (minGap <= 0) return null;

  const startMin = dateToMinutes(proposedStart);
  const endMin = dateToMinutes(proposedEnd);
  const openMin = timeToMinutes(hallOpenTime);
  const closeMin = timeToMinutes(hallCloseTime);

  // Get same-day, same-court bookings (excluding cancelled)
  const proposedDateStr = proposedStart.toISOString().slice(0, 10);
  const courtBookings = existingBookings
    .filter(
      (b) =>
        b.court_id === courtId &&
        b.status !== "cancelled" &&
        new Date(b.start_time).toISOString().slice(0, 10) === proposedDateStr
    )
    .map((b) => ({
      start: dateToMinutes(new Date(b.start_time)),
      end: dateToMinutes(new Date(b.end_time)),
    }));

  // Add proposed booking and sort
  const allBookings = [...courtBookings, { start: startMin, end: endMin }].sort(
    (a, b) => a.start - b.start
  );

  // Check gap before first booking (from hall opening)
  const gapBeforeFirst = allBookings[0].start - openMin;
  if (gapBeforeFirst > 0 && gapBeforeFirst < minGap) {
    return `Bokningen skapar en ${gapBeforeFirst}-minuterslucka från öppning. Minst ${minGap} min krävs.`;
  }

  // Check gaps between bookings
  for (let i = 0; i < allBookings.length - 1; i++) {
    const gap = allBookings[i + 1].start - allBookings[i].end;
    if (gap > 0 && gap < minGap) {
      return `Bokningen skapar en ${gap}-minuterslucka. Minst ${minGap} min krävs.`;
    }
  }

  // Check gap after last booking (to hall closing)
  const last = allBookings[allBookings.length - 1];
  const gapAfterLast = closeMin - last.end;
  if (gapAfterLast > 0 && gapAfterLast < minGap) {
    return `Bokningen skapar en ${gapAfterLast}-minuterslucka till stängning. Minst ${minGap} min krävs.`;
  }

  return null;
}

/**
 * Check if a specific slot would create a gap, used for greying out slots.
 * Uses a default duration (minGap) to simulate a booking.
 */
export function wouldSlotCreateGap(
  courtId: string,
  slotStart: Date,
  defaultDurationMin: number,
  existingBookings: Array<{ court_id: string; start_time: string; end_time: string; status?: string }>,
  hallOpenTime: string,
  hallCloseTime: string,
  minGapMinutes: number
): boolean {
  if (minGapMinutes <= 0) return false;

  const slotEnd = new Date(slotStart.getTime() + defaultDurationMin * 60000);
  const err = validateGapProtection(
    courtId,
    slotStart,
    slotEnd,
    existingBookings,
    hallOpenTime,
    hallCloseTime,
    { gap_protection: true, min_gap_minutes: minGapMinutes }
  );
  return err !== null;
}
